import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { CARD_ACCENTS, DEFAULT_ACCENT } from "@/lib/cardThemes";
import { Check, ImagePlus, Loader2, Trash2 } from "lucide-react";

/**
 * Controlled card-style picker: accent swatches + optional cover image.
 * Parent owns persistence (immediate save on My Card; form-bound on Profile).
 */
export function CardCustomizer({ accent = DEFAULT_ACCENT, cover = "", onAccentChange, onCoverChange }) {
  const [uploading, setUploading] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Please choose an image under 10MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("prefix", "covers");
      const res = await api.post("/storage/upload", fd);
      onCoverChange?.(res.data.url);
      toast.success("Cover image added");
    } catch (err) {
      toast.error("Upload failed — please try again");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const activeAccent = accent || DEFAULT_ACCENT;

  return (
    <div className="space-y-4" data-testid="card-customizer">
      <div>
        <Label className="mb-2 block">Accent color</Label>
        <div className="flex flex-wrap gap-2">
          {Object.entries(CARD_ACCENTS).map(([key, a]) => {
            const on = activeAccent === key;
            return (
              <button
                type="button"
                key={key}
                onClick={() => onAccentChange?.(key)}
                title={a.label}
                aria-label={a.label}
                aria-pressed={on}
                data-testid={`accent-${key}`}
                className={`flex h-9 w-9 items-center justify-center rounded-full ring-2 ring-offset-2 ring-offset-background transition-transform hover:scale-105 focus-visible:outline-none focus-visible:ring-foreground ${on ? "ring-foreground" : "ring-transparent"}`}
                style={{ backgroundColor: a.swatch }}
              >
                {on && <Check className="h-4 w-4 text-white" />}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <Label className="mb-2 block">Cover image <span className="text-muted-foreground">(optional)</span></Label>
        {cover ? (
          <div className="space-y-2">
            <div className="relative h-20 w-full overflow-hidden rounded-lg border border-border">
              <img src={cover} alt="Card cover preview" className="h-full w-full object-cover" />
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="secondary" size="sm" disabled={uploading} asChild>
                <label htmlFor="fc-cover-input" className="cursor-pointer">
                  {uploading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <ImagePlus className="mr-1.5 h-4 w-4" />}Replace
                </label>
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={() => onCoverChange?.("")} className="text-muted-foreground hover:text-destructive" data-testid="card-cover-remove">
                <Trash2 className="mr-1.5 h-4 w-4" />Remove
              </Button>
            </div>
          </div>
        ) : (
          <Button type="button" variant="secondary" size="sm" disabled={uploading} asChild>
            <label htmlFor="fc-cover-input" className="cursor-pointer" data-testid="card-cover-upload">
              {uploading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <ImagePlus className="mr-1.5 h-4 w-4" />}Add cover image
            </label>
          </Button>
        )}
        <input id="fc-cover-input" type="file" accept="image/*" className="hidden" onChange={upload} data-testid="card-cover-input" />
      </div>
    </div>
  );
}
